import { gql } from "../gql";

// Customer fragments
export const CUSTOMER_CORE_FRAGMENT = gql(`
  fragment CustomerCore on Customer {
    id
    franchiseId
    email
    firstName
    lastName
    totalPoints
    totalVisits
    totalSpent
    isActive
    createdAt
    updatedAt
    phoneNumbers {
      id
      phone
      isPrimary
      isActive
      label
    }
    primaryPhone {
      id
      phone
      label
    }
  }
`);

export const CUSTOMER_TIER_FRAGMENT = gql(`
  fragment CustomerTier on LoyaltyTier {
    id
    name
    description
    minPoints
    maxPoints
    priorityBooking
    color
  }
`);

export const CUSTOMER_WITH_TIER_FRAGMENT = gql(`
  fragment CustomerWithTier on Customer {
    ...CustomerCore @unmask
    currentTier {
      ...CustomerTier @unmask
    }
  }
`);

export const CUSTOMER_DETAIL_FRAGMENT = gql(`
  fragment CustomerDetail on Customer {
    ...CustomerWithTier @unmask
    dateOfBirth
    avatar
    averageSpent
    lastVisitDate
    notes
    currentTierId
  }
`);

// Worker fragments
export const WORKER_USER_FRAGMENT = gql(`
  fragment WorkerUser on User {
    id
    email
    firstName
    lastName
    role
    isActive
    emailVerified
  }
`);

export const WORKER_CORE_FRAGMENT = gql(`
  fragment WorkerCore on Worker {
    id
    userId
    franchiseId
    displayName
    color
    isActive
    canAcceptOnlineBookings
    createdAt
    updatedAt
  }
`);

export const WORKER_WITH_USER_FRAGMENT = gql(`
  fragment WorkerWithUser on Worker {
    ...WorkerCore @unmask
    user {
      ...WorkerUser @unmask
    }
  }
`);

export const WORKER_DETAIL_FRAGMENT = gql(`
  fragment WorkerDetail on Worker {
    ...WorkerWithUser @unmask
    bio
    profileImageUrl
    workerSalons {
      id
      salonId
    }
    salonRole
  }
`);

// Service fragments
export const SERVICE_CATEGORY_FRAGMENT = gql(`
  fragment ServiceCategory on ServiceCategory {
    id
    name
    description
    color
    icon
    sortOrder
    isArchived
    allowOnlineBooking
  }
`);

export const SERVICE_PRICING_FRAGMENT = gql(`
  fragment ServicePricing on ServicePricing {
    id
    price
    effectiveFrom
    effectiveTo
    createdAt
  }
`);

export const SERVICE_CORE_FRAGMENT = gql(`
  fragment ServiceCore on Service {
    id
    catalogId
    categoryId
    serviceCode
    name
    description
    duration
    allowOnlineBooking
    pricingMode
    basePrice
    templateId
    storefrontDisplayMode
    isPopular
    isActive
    createdAt
    updatedAt
  }
`);

export const SERVICE_WITH_CATEGORY_FRAGMENT = gql(`
  fragment ServiceWithCategory on Service {
    ...ServiceCore @unmask
    category {
      ...ServiceCategory @unmask
    }
  }
`);

// Service option fragments
export const SERVICE_OPTION_FRAGMENT = gql(`
  fragment ServiceOption on ServiceOption {
    id
    groupId
    optionName
    priceModifier
    durationModifier
    sortOrder
    isActive
    isDefault
    createdAt
    updatedAt
  }
`);

export const SERVICE_OPTION_GROUP_FRAGMENT = gql(`
  fragment ServiceOptionGroup on ServiceOptionGroup {
    id
    serviceId
    name
    selectionType
    minSelections
    maxSelections
    displayStyle
    sortOrder
    isDeleted
    createdAt
    updatedAt
    options {
      ...ServiceOption @unmask
    }
  }
`);

export const PRICE_BREAKDOWN_ITEM_FRAGMENT = gql(`
  fragment PriceBreakdownItem on PriceBreakdownItem {
    item
    price
  }
`);

export const PRICE_CALCULATION_FRAGMENT = gql(`
  fragment PriceCalculation on PriceCalculation {
    basePrice
    optionsTotal
    finalPrice
    selectedOptions {
      ...ServiceOption @unmask
    }
    breakdown {
      ...PriceBreakdownItem @unmask
    }
  }
`);

export const PRICE_RANGE_FRAGMENT = gql(`
  fragment PriceRange on PriceRange {
    minPrice
    maxPrice
  }
`);

export const SERVICE_PRICE_DISPLAY_FRAGMENT = gql(`
  fragment ServicePriceDisplay on ServicePriceDisplay {
    mode
    fixedPrice
    priceRange {
      ...PriceRange @unmask
    }
    requiresQuote
  }
`);

export const SERVICE_DETAIL_FRAGMENT = gql(`
  fragment ServiceDetail on Service {
    ...ServiceWithCategory @unmask
    servicePricing {
      ...ServicePricing @unmask
    }
    serviceOptionGroups {
      ...ServiceOptionGroup @unmask
    }
    displayPrice {
      ...ServicePriceDisplay @unmask
    }
  }
`);

// Appointment fragments
export const APPOINTMENT_SERVICE_FRAGMENT = gql(`
  fragment AppointmentService on AppointmentService {
    id
    serviceId
    serviceName
    serviceDescription
    totalPrice
    originalPrice
    discountAmount
    serviceDuration
    serviceCategoryName
    sequenceOrder
    pricingMode
    basePrice
    optionsTotal
    workerId
    worker {
      ...WorkerWithUser @unmask
    }
    selectedOptions {
      id
      groupName
      optionName
      priceModifier
    }
  }
`);

export const APPOINTMENT_SERVICE_WITH_DETAIL_FRAGMENT = gql(`
  fragment AppointmentServiceWithDetail on AppointmentService {
    ...AppointmentService @unmask
    service {
      id
      name
      description
    }
  }
`);

export const APPOINTMENT_CORE_FRAGMENT = gql(`
  fragment AppointmentCore on Appointment {
    id
    salonId
    customerId
    startTime
    endTime
    totalPrice
    originalTotalPrice
    bookingStatus
    outcome
    paymentMethod
    customerMessage
    notes
    trafficSourceId
    trafficSource {
      id
      code
      name
      description
    }
  }
`);

export const APPOINTMENT_WITH_CUSTOMER_FRAGMENT = gql(`
  fragment AppointmentWithCustomer on Appointment {
    ...AppointmentCore @unmask
    customer {
      ...CustomerWithTier @unmask
    }
  }
`);

// Note: Worker information is now available through appointmentServices

export const APPOINTMENT_LIST_FRAGMENT = gql(`
  fragment AppointmentList on Appointment {
    ...AppointmentCore @unmask
    customer {
      ...CustomerWithTier @unmask
    }
    appointmentServices {
      ...AppointmentService @unmask
    }
  }
`);

export const APPOINTMENT_DETAIL_FRAGMENT = gql(`
  fragment AppointmentDetail on Appointment {
    ...AppointmentCore @unmask
    internalNotes
    cancellationReasonType
    cancellationReasonText
    guestCancellationReason
    guestCancellationText
    bookingPhone {
      id
      phone
      label
      isPrimary
    }
    customer {
      ...CustomerDetail @unmask
    }
    appointmentServices {
      ...AppointmentServiceWithDetail @unmask
    }
  }
`);

// Legacy WorkerSchedule and WorkerSpecialty fragments removed

// Point reward fragments
export const POINT_REWARD_FRAGMENT = gql(`
  fragment PointReward on PointReward {
    id
    points
    type
    description
    totalPointsAfter
    createdAt
  }
`);

// Pagination fragment
export const PAGINATION_FRAGMENT = gql(`
  fragment Pagination on PaginationInfo {
    page
    limit
    total
    totalPages
    hasNext
    hasPrevious
  }
`);

// Shift Template fragments
export const TEMPLATE_TIME_INTERVAL_FRAGMENT = gql(`
  fragment TemplateTimeInterval on TemplateTimeInterval {
    id
    startTime
    endTime
    sortOrder
  }
`);

export const TEMPLATE_BREAK_RULE_FRAGMENT = gql(`
  fragment TemplateBreakRule on TemplateBreakRule {
    id
    afterHours
    breakType
    duration
    isPaid
    isRequired
    sortOrder
  }
`);

export const SHIFT_TEMPLATE_FRAGMENT = gql(`
  fragment ShiftTemplate on ShiftTemplate {
    id
    name
    description
    position
    scheduleType
    days
    color
    icon
    sortOrder
    isActive
    franchiseId
    createdAt
    updatedAt
    timeIntervals {
      ...TemplateTimeInterval @unmask
    }
    breakRules {
      ...TemplateBreakRule @unmask
    }
  }
`);
